/**
 * Deterministic hash noise. Everything the world generator needs is derived from
 * (WORLD_SEED, coordinate) so a chunk always regenerates identically (PRD §10).
 */

const F = 1 / 4294967296;

export function hash1(x: number, seed = 0): number {
  let h = (x | 0) * 374761393 + seed * 668265263;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) * F;
}

export function hash2(x: number, y: number, seed = 0): number {
  let h = (x | 0) * 374761393 + (y | 0) * 668265263 + seed * 2147483647;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) * F;
}

const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);

/** Value noise in [-1, 1]. */
export function noise1(x: number, seed = 0): number {
  const i = Math.floor(x);
  const f = fade(x - i);
  return (hash1(i, seed) * (1 - f) + hash1(i + 1, seed) * f) * 2 - 1;
}

/** Value noise in [-1, 1]. */
export function noise2(x: number, y: number, seed = 0): number {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = fade(x - ix), fy = fade(y - iy);
  const a = hash2(ix, iy, seed), b = hash2(ix + 1, iy, seed);
  const c = hash2(ix, iy + 1, seed), d = hash2(ix + 1, iy + 1, seed);
  const top = a * (1 - fx) + b * fx;
  const bot = c * (1 - fx) + d * fx;
  return (top * (1 - fy) + bot * fy) * 2 - 1;
}

export function fbm1(x: number, octaves = 4, seed = 0, lacunarity = 2.03, gain = 0.5): number {
  let sum = 0, amp = 1, norm = 0, freq = 1;
  for (let o = 0; o < octaves; o++) {
    sum += noise1(x * freq, seed + o * 131) * amp;
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}

export function fbm2(x: number, y: number, octaves = 4, seed = 0, lacunarity = 2.03, gain = 0.5): number {
  let sum = 0, amp = 1, norm = 0, freq = 1;
  for (let o = 0; o < octaves; o++) {
    sum += noise2(x * freq, y * freq, seed + o * 131) * amp;
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}

/** Ridged noise in [0, 1] — good for rock strata and cliff faces. */
export function ridge2(x: number, y: number, octaves = 4, seed = 0): number {
  let sum = 0, amp = 1, norm = 0, freq = 1;
  for (let o = 0; o < octaves; o++) {
    sum += (1 - Math.abs(noise2(x * freq, y * freq, seed + o * 197))) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2.07;
  }
  return sum / norm;
}

/** Small deterministic PRNG for one-shot placement decisions. */
export class Rng {
  private s: number;
  constructor(seed: number) {
    this.s = (seed | 0) || 1;
  }
  next(): number {
    this.s = (Math.imul(this.s ^ (this.s >>> 15), 2246822519) + 3266489917) | 0;
    let t = this.s >>> 0;
    t = (t ^ (t >>> 13)) >>> 0;
    return t * F;
  }
  range(a: number, b: number): number {
    return a + (b - a) * this.next();
  }
  int(a: number, b: number): number {
    return Math.floor(this.range(a, b + 1));
  }
  pick<T>(arr: readonly T[]): T {
    return arr[Math.min(arr.length - 1, Math.floor(this.next() * arr.length))];
  }
}
